import React from "react";
import { Linking, TouchableOpacity } from "react-native";
import styled from "styled-components/native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { ToastProps } from "@components/molecules/VDBToast/types";
import { VDBText } from "@components/atoms";
import Toast from "react-native-toast-message";
import { useTheme } from "styled-components/native";
import { css } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { View } from "@gluestack-ui/themed";
import { VDBIcons } from "@utils/VDBIcons";
import {
  getToastColor,
  getToastIcon,
} from "@components/molecules/VDBToast/util";
import { SpaceType } from "@components/atoms/Spacer/types";
import ProgressBar from "@components/atoms/ProgressBar";
import { ExportStatus } from "@src/types/ExportsResult";

const VDBToast = ({ text1, text2, props }: ToastProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const {
    type,
    iconEnabled,
    closeIconEnabled,
    onNotificationPress,
    showProgressBar,
    progress,
    downloadUrl,
    cancelExport,
  } = props;

  const ToastIcon = getToastIcon(type);

  const handleCloseToast = () => {
    if (cancelExport) {
      cancelExport(ExportStatus.hidden);
    } else {
      Toast.hide();
    }
  };

  const handlePress = () => {
    if (onNotificationPress) {
      onNotificationPress();
    }
    Toast.hide();
  };

  return (
    <OverlayContainer
      onPress={onNotificationPress ? handlePress : undefined}
      showProgressBar={showProgressBar}
    >
      <Overlay softShadow="1" type={type} theme={theme}>
        <ToastContent>
          {iconEnabled && (
            <IconFrame type={type}>
              <VDBIcon
                name={ToastIcon}
                variant={IconsVariant.VDBCustom}
                size={24}
                color={getToastColor(type, theme)}
              />
            </IconFrame>
          )}
          <TextContent>
            {text1 && (
              <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                {text1}
              </VDBText>
            )}
            {text2 && typeof text2 === "string" ? (
              <VDBText
                color={theme["core-06"]}
                type={typography["Minor-R"]}
                onPress={() => {
                  if (downloadUrl) {
                    if (cancelExport) {
                      cancelExport(ExportStatus.downloaded);
                    }
                    Linking.openURL(downloadUrl);
                  } else {
                    handleCloseToast();
                  }
                }}
              >
                {text2}
              </VDBText>
            ) : (
              text2 && text2
            )}
            {showProgressBar && (
              <>
                <ProgressBar
                  progress={progress}
                  backgroundColor={theme["core-03"]}
                  progressColor={theme["success-01"]}
                />
              </>
            )}
          </TextContent>
          {closeIconEnabled && (
            <IconContainer>
              <TouchableOpacity onPress={handleCloseToast}>
                <VDBIcon
                  name={VDBIcons.CLOSE_CIRCLE}
                  variant={IconsVariant.VDBCustom}
                  size={20}
                  color={theme["core-04"]}
                />
              </TouchableOpacity>
            </IconContainer>
          )}
        </ToastContent>
      </Overlay>
    </OverlayContainer>
  );
};

const OverlayContainer = styled.Pressable<{
  showProgressBar: boolean;
}>`
  flex: 1;
  width: 100%;
  ${({ theme }) =>
    theme.isMobile
      ? css`
          padding-horizontal: ${SpaceType.Double}px;
        `
      : css`
          transform: translateY(-75px);
        `}
  ${({ theme, showProgressBar }) =>
    !theme.isMobile &&
    css`
      max-width: ${showProgressBar ? 608 : 423}px;
    `}
`;

const Overlay = styled(View)<{ type: string; theme: any }>`
  background-color: ${({ theme }) => theme["core-01"]};
  border-color: ${({ type, theme }) => getToastColor(type, theme)};
  border-width: 2px;
  padding: 16px;
  border-radius: 8px;
`;

const ToastContent = styled.View`
  flex-direction: row;
  align-items: center;
`;

const TextContent = styled.View`
  flex: 1;
  gap: ${SpaceType.ExtraSmall}px;
`;

const IconFrame = styled.View<{ type: string }>`
  padding: 8px;
  margin-right: 8px;
  border-radius: 40px;
  background-color: ${({ type, theme }) =>
    type === "error"
      ? theme["error-02"]
      : type === "success" || type === "notification"
      ? theme["success-02"]
      : type === "download"
      ? theme["warn-02"]
      : theme["warn-02"]};
`;

const IconContainer = styled.View`
  top: 2px;
  left: 2px;
  align-items: flex-end;
`;

export const toastConfig = {
  vdbToast: (props: ToastProps) => <VDBToast {...props} />,
};

export default VDBToast;
